import {
  CreditCard,
  Crown,
  Plus,
  Trash2,
} from "lucide-react";
import { useState } from "react";

import {
  Alert,
  AlertDescription,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  useDeletePaymentMethod,
  useListPaymentMethods,
} from "@/hooks/payment";
import { stripePromise } from "@/hooks/payment/useStripe";
import { create } from "@bufbuild/protobuf";
import { PaymentMethodPathSchema } from "@generated/proto/model";
import { Elements } from "@stripe/react-stripe-js";

import { PaymentMethodForm } from "./PaymentMethodForm";

import type {
  UserPath
} from "@generated/proto/model";
interface PaymentMethodsListProps {
  userPath: UserPath;
  onDelete: (paymentMethodId: string) => void;
}

export function PaymentMethodsList({ userPath, onDelete }: PaymentMethodsListProps) {
  const { data: paymentMethods = [], isLoading, error } = useListPaymentMethods(userPath);
  const deletePaymentMethod = useDeletePaymentMethod();
  const [showAddForm, setShowAddForm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (paymentMethodId: string) => {
    if (!confirm('Are you sure you want to delete this payment method?')) {
      return;
    }

    setDeletingId(paymentMethodId);
    try {
      const paymentMethodPath = create(PaymentMethodPathSchema, {
        userId: userPath.userId,
        paymentMethodId,
      });
      await deletePaymentMethod.mutateAsync(paymentMethodPath);
      onDelete(paymentMethodId);
    } catch (err) {
      console.error('Failed to delete payment method:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const getCardBrandIcon = (brand: string) => {
    // You could add specific brand icons here
    return <CreditCard style={{ width: '20px', height: '20px' }} />;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load payment methods. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between p-2">
        <CardTitle className="text-sm font-medium font-sans">Payment Methods</CardTitle>
        <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="h-4 text-xs gap-0">
              <Plus style={{ width: '20px', height: '20px' }} />
              Add New
            </Button>
          </DialogTrigger>
          <DialogContent>
            <Elements stripe={stripePromise}>
              <PaymentMethodForm
                userPath={userPath}
                onSuccess={() => setShowAddForm(false)}
                onCancel={() => setShowAddForm(false)}
                setAsDefault={paymentMethods.length === 0}
              />
            </Elements>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="px-2 pb-2">
        <div className="space-y-1">
          {paymentMethods.length === 0 ? (
            <div className="text-center py-2 text-gray-500">
              <CreditCard style={{ width: '20px', height: '20px' }} className="mx-auto mb-1 opacity-50" />
              <p className="text-sm font-medium mb-1 font-sans">No payment methods</p>
              <p className="text-xs font-sans">Add a payment method to make purchases easier.</p>
            </div>
          ) : (
            paymentMethods.map((paymentMethod) => (
              <div
                key={paymentMethod.paymentMethodId}
                className="flex items-center justify-between p-2 border rounded-lg"
              >
                <div className="flex items-center gap-2">
                  {getCardBrandIcon(paymentMethod.card?.brand || '')}
                  <div>
                    <div className="font-medium flex items-center gap-1 text-sm font-sans">
                      {paymentMethod.card?.brand?.toUpperCase() || 'Card'} ••••{paymentMethod.card?.last4}
                      {paymentMethod.isDefault && (
                        <Crown style={{ width: '20px', height: '20px' }} className="text-yellow-500" />
                      )}
                    </div>
                    <div className="text-xs text-gray-500 font-sans">
                      Expires {paymentMethod.card?.expMonth}/{paymentMethod.card?.expYear}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {paymentMethod.isDefault && (
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded">
                      Default
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => handleDelete(paymentMethod.paymentMethodId)}
                    disabled={deletingId === paymentMethod.paymentMethodId}
                  >
                    <Trash2 style={{ width: '20px', height: '20px' }} />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}